import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { auth } from '../firebase-config';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AuthVerifyPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmailLink = async () => {
      // Check if the link is valid
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        setStatus('error');
        setErrorMessage('Invalid or expired magic link.');
        return;
      }

      // Get the email from localStorage
      let email = window.localStorage.getItem('emailForSignIn');

      // If email is not in localStorage, prompt user
      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
      }

      if (!email) {
        setStatus('error');
        setErrorMessage('Email is required to complete sign in.');
        return;
      }

      try {
        // Set persistence to keep user logged in across browser sessions
        await setPersistence(auth, browserLocalPersistence);

        // Sign in with the email link
        const result = await signInWithEmailLink(auth, email, window.location.href);

        // Clear email from localStorage
        window.localStorage.removeItem('emailForSignIn');

        console.log('Sign in successful:', result.user);
        setStatus('success');

        // Check if there's a redirect URL stored (e.g., from invitation flow)
        const redirectUrl = window.localStorage.getItem('redirectAfterAuth');
        console.log('🔍 Checking for redirect URL:', redirectUrl);

        if (redirectUrl) {
          console.log('✅ Found redirect URL, will redirect to:', redirectUrl);
          window.localStorage.removeItem('redirectAfterAuth');
          // Redirect to stored URL after 2 seconds
          setTimeout(() => {
            console.log('🚀 Redirecting to:', redirectUrl);
            navigate(redirectUrl);
          }, 2000);
        } else {
          console.log('ℹ️ No redirect URL found, redirecting to dashboard');
          // Default redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } catch (error: any) {
        console.error('Error signing in with email link:', error);
        setStatus('error');
        setErrorMessage(
          error.code === 'auth/invalid-action-code'
            ? 'This magic link is invalid or has expired.'
            : error.code === 'auth/expired-action-code'
            ? 'This magic link has expired. Please request a new one.'
            : 'Failed to sign in. Please try again.'
        );
      }
    };

    verifyEmailLink();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="bg-card border border-border rounded-lg p-8 shadow-sm text-center space-y-6">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto" />
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-foreground">
                  Verifying your email...
                </h2>
                <p className="text-muted-foreground">
                  Please wait while we sign you in.
                </p>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-foreground">
                  Success!
                </h2>
                <p className="text-muted-foreground">
                  You've been signed in successfully.
                  <br />
                  Redirecting you to the app...
                </p>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-destructive mx-auto" />
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-foreground">
                  Verification Failed
                </h2>
                <p className="text-muted-foreground">{errorMessage}</p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Request New Magic Link
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
